import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Simple cache for user profile data
const userProfileCache = {
  data: null,
  timestamp: null,
  ttl: 5 * 60 * 1000, // 5 minutes cache
  
  get() {
    if (this.data && this.timestamp && (Date.now() - this.timestamp < this.ttl)) {
      return this.data;
    }
    return null;
  },
  
  set(data) {
    this.data = data;
    this.timestamp = Date.now();
  },
  
  clear() {
    this.data = null;
    this.timestamp = null;
  }
};

// Centralized token fetching function
const getAuthToken = async () => {
  try {
    const { getAuth0Client } = await import('../config/auth0');
    const auth0Client = await getAuth0Client();
    
    const isAuthenticated = await auth0Client.isAuthenticated();
    if (isAuthenticated) {
      return await auth0Client.getTokenSilently();
    }
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  return null;
};

// Request interceptor to add auth token
api.interceptors.request.use(async (config) => {
  const accessToken = await getAuthToken();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Response interceptor for consistent error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log errors for debugging
    console.error('API Error:', error);
    
    // Handle common error scenarios
    if (error.response?.status === 401) {
      // Clear user cache on authentication errors
      userProfileCache.clear();
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const getCurrentUser = async () => {
  try {
    // Check cache first
    const cachedUser = userProfileCache.get();
    if (cachedUser) {
      return cachedUser;
    }
    
    const response = await api.get('/auth/user');
    
    // Cache the response
    userProfileCache.set(response.data);
    
    return response.data;
  } catch (error) {
    console.error('Failed to get current user:', error);
    throw error;
  }
};

// Clear user cache when needed (e.g., on logout)
export const clearUserCache = () => {
  userProfileCache.clear();
};

// Projects API
export const getProjects = async (userId) => {
  const response = await api.get(`/projects?userId=${userId}`);
  return response.data?.data || [];
};

export const getProject = async (projectId) => {
  const response = await api.get(`/projects/${projectId}`);
  return response.data?.data || response.data;
};

export const createProject = async (projectData) => {
  const response = await api.post('/projects', projectData);
  return response.data?.data || response.data;
};

export const updateProject = async (projectId, projectData) => {
  const response = await api.put(`/projects/${projectId}`, projectData);
  return response.data?.data || response.data;
};

export const deleteProject = async (projectId) => {
  const response = await api.delete(`/projects/${projectId}`);
  return response.data;
};

// Prompts API
export const getPrompts = async (projectId) => {
  const response = await api.get(`/prompts/project/${projectId}`);
  return response.data?.data || [];
};

export const getSharedPrompts = async (userId) => {
  const response = await api.get(`/prompts/shared/${userId}`);
  return response.data;
};

export const getPrompt = async (promptId) => {
  const response = await api.get(`/prompts/${promptId}`);
  return response.data?.data || response.data;
};

export const createPrompt = async (promptData) => {
  const response = await api.post('/prompts', promptData);
  return response.data;
};

export const createPromptVersion = async (promptId, versionData) => {
  const response = await api.post(`/prompts/${promptId}/versions`, versionData);
  return response.data;
};

export const activatePromptVersion = async (promptId, versionId) => {
  const response = await api.patch(`/prompts/${promptId}/versions/${versionId}/activate`);
  return response.data;
};

// Update a prompt version (amend)
export const updatePromptVersion = async (promptId, versionId, versionData) => {
  const response = await api.patch(`/prompts/${promptId}/versions/${versionId}`, versionData);
  return response.data;
};

// Delete a prompt version
export const deletePromptVersion = async (promptId, versionId) => {
  const response = await api.delete(`/prompts/${promptId}/versions/${versionId}`);
  return response.data;
};

export const getPromptDiff = async (promptId, version1, version2) => {
  const response = await api.get(`/prompts/${promptId}/diff/${version1}/${version2}`);
  return response.data;
};

// Test Sets API
export const getTestSets = async (projectId) => {
  const response = await api.get(`/test-sets/project/${projectId}`);
  return response.data?.data || [];
};

export const createTestSet = async (testSetData) => {
  const response = await api.post('/test-sets', testSetData);
  return response.data?.data || response.data;
};

export const getTestSet = async (testSetId) => {
  const response = await api.get(`/test-sets/${testSetId}`);
  return response.data?.data || response.data;
};

export const addTestCase = async (testSetId, testCaseData) => {
  const response = await api.post(`/test-sets/${testSetId}/cases`, testCaseData);
  return response.data;
};

export const runTests = async (testSetId, promptVersionId, modelId, selectedTestCases = []) => {
  const response = await api.post(`/test-sets/${testSetId}/run/${promptVersionId}`, { 
    modelId, 
    selectedTestCases 
  });
  return response.data;
};

// Consolidated model API using external-apis route
export const getAvailableModels = async () => {
  try {
    const response = await api.get('/models/available');
    return response.data;
  } catch (error) {
    console.error('Error fetching available models:', error);
    return [
      // Default models (internal)
      {
        id: 'llama-3.1-8b-instant',
        name: 'Llama 3 8B',
        provider: 'Groq',
        description: 'Fast, efficient model',
        type: 'internal'
      },
      {
        id: 'llama-3.3-70b-versatile',
        name: 'Llama 3 70B',
        provider: 'Groq',
        description: 'High accuracy model',
        type: 'internal'
      },
      {
        id: 'gemma2-9b-it',
        name: 'Gemma 2',
        provider: 'Groq',
        description: 'Instruction-tuned model',
        type: 'internal'
      }
    ];
  }
};

export const getAvailableModelsWithApiKeys = async (userApiKeys = {}) => {
  try {
    const response = await api.post('/models/available-with-keys', { 
      apiKeys: userApiKeys 
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching available models with API keys:', error);
    return getAvailableModels(); // Fallback to default models
  }
};

export const validateApiKey = async (provider, apiKey) => {
  try {
    const response = await api.post('/validate-api-key', { 
      provider, 
      apiKey 
    });
    return response.data.success;
  } catch (error) {
    console.error(`Error validating ${provider} API key:`, error);
    return false;
  }
};

export const testApiKey = async (provider, apiKey, testMessage = 'Hello, this is a test.') => {
  try {
    const response = await api.post('/test-api-key', { 
      provider, 
      apiKey, 
      testMessage 
    });
    return response.data;
  } catch (error) {
    console.error(`Error testing ${provider} API key:`, error);
    return { success: false, error: error.message };
  }
};

export const runPromptWithApiKey = async (promptId, inputs, model, apiKeys = {}) => {
  try {
    const response = await api.post('/prompts/run', { 
      promptId, 
      inputs, 
      model,
      apiKeys 
    });
    return response.data;
  } catch (error) {
    console.error('Error running prompt with API key:', error);
    throw error;
  }
};

export const runEvaluationWithApiKey = async (testSetId, config, apiKeys = {}) => {
  try {
    const response = await api.post('/evaluations/run', { 
      testSetId, 
      config: {
        ...config,
        apiKeys
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error running evaluation with API key:', error);
    throw error;
  }
};

export const getPromptEvaluationConfig = async (promptId) => {
  try {
    const response = await api.get(`/prompts/${promptId}/evaluation-config`);
    return response.data;
  } catch (error) {
    console.error('Error fetching prompt evaluation config:', error);
    return {
      auto_evaluation_enabled: false,
      evaluation_model: 'llama-3.1-8b-instant',
      pass_threshold: 0.7,
      evaluation_criteria: [],
      custom_evaluation_prompt: ''
    };
  }
};

export const updatePromptEvaluationConfig = async (promptId, config) => {
  try {
    const response = await api.put(`/prompts/${promptId}/evaluation-config`, config);
    return response.data;
  } catch (error) {
    console.error('Error updating prompt evaluation config:', error);
    throw error;
  }
};

export const getEvaluationConfig = async (testSetId) => {
  try {
    const response = await api.get(`/test-sets/${testSetId}/evaluation-config`);
    return response.data;
  } catch (error) {
    console.error('Error fetching evaluation config:', error);
    return {
      auto_evaluation_enabled: true,
      evaluation_model: 'llama-3.1-8b-instant',
      pass_threshold: 0.7,
      evaluation_criteria: [],
      custom_evaluation_prompt: ''
    };
  }
};

export const updateEvaluationConfig = async (testSetId, config) => {
  try {
    const response = await api.put(`/test-sets/${testSetId}/evaluation-config`, config);
    return response.data;
  } catch (error) {
    console.error('Error updating evaluation config:', error);
    throw error;
  }
};

export const getTestRuns = async (testSetId, promptVersionId) => {
  const response = await api.get(`/test-sets/${testSetId}/runs/${promptVersionId}`);
  return response.data;
};

export const getTestRunHistory = async (testSetId) => {
  const response = await api.get(`/test-sets/${testSetId}/runs`);
  return response.data;
};

export const submitEvaluation = async (runId, evaluationData) => {
  const response = await api.post(`/test-sets/runs/${runId}/evaluate`, evaluationData);
  return response.data;
};

export const getEvaluationTags = async (organizationId) => {
  const response = await api.get(`/test-sets/tags/${organizationId}`);
  return response.data;
};

export const createEvaluationTag = async (tagData) => {
  const response = await api.post('/test-sets/tags', tagData);
  return response.data;
};

// Auto-evaluation API
export const triggerAutoEvaluation = async (runId, overrideConfig = {}) => {
  const response = await api.post(`/test-sets/runs/${runId}/auto-evaluate`, { overrideConfig });
  return response.data;
};

export const applyManualOverride = async (runId, status, reason) => {
  const response = await api.post(`/test-sets/runs/${runId}/override`, { status, reason });
  return response.data;
};

// Logs API
export const getLogsAnalytics = async (organizationId, days = 7) => {
  const response = await api.get(`/logs/analytics/${organizationId}?days=${days}`);
  return response.data;
};

// API Keys management
export const getApiKeys = async (organizationId) => {
  const response = await api.get(`/logs/api-keys/${organizationId}`);
  return response.data;
};

export const createApiKey = async (organizationId, keyName, projectId = null) => {
  const response = await api.post('/logs/api-keys', { organizationId, keyName, projectId });
  return response.data;
};

export const deactivateApiKey = async (keyId) => {
  const response = await api.patch(`/logs/api-keys/${keyId}/deactivate`);
  return response.data;
};

export const togglePromptLogging = async (promptId, enableLogging) => {
  const response = await api.patch(`/prompts/${promptId}/logging`, { enableLogging });
  return response.data;
};

// MVP Feature 8: Auto-evaluation API
export const getAutoEvalSettings = async (projectId) => {
  const response = await api.get(`/auto-eval/settings/project/${projectId}`);
  return response.data;
};

export const updateAutoEvalSettings = async (projectId, settings) => {
  const response = await api.put(`/auto-eval/settings/project/${projectId}`, settings);
  return response.data;
};

// Organization-level logs (existing function, kept for backward compatibility)
export const getOrganizationLogs = async (organizationId, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, value);
    }
  });
  
  const response = await api.get(`/logs/organization/${organizationId}?${params}`);
  return response.data;
};

// Prompt Suggestions API
export const generatePromptSuggestion = async (promptId, testRunId, stream = false, logId = null) => {
  const requestBody = { stream };
  
  if (testRunId) {
    requestBody.testRunId = testRunId;
  }
  
  if (logId) {
    requestBody.logId = logId;
  }

  if (stream) {
    // For streaming with axios, we need to handle it differently
    try {
      const response = await api.post(`/prompts/${promptId}/suggest`, requestBody, {
        responseType: 'stream'
      });
      return response; // Return the response object for streaming
    } catch (error) {
      console.error('Error with streaming suggestion:', error);
      throw error;
    }
  } else {
    const response = await api.post(`/prompts/${promptId}/suggest`, requestBody);
    return response.data;
  }
};

// ================================
// MVP Feature 6: Git-like Collaboration API
// ================================

// Branches API
export const getBranches = async (promptId, userId) => {
  const response = await api.get(`/branches/prompt/${promptId}?userId=${userId}`);
  return response.data?.data || [];
};

export const createBranch = async (branchData) => {
  const response = await api.post('/branches', branchData);
  return response.data;
};

export const getBranch = async (branchId, userId) => {
  const response = await api.get(`/branches/${branchId}?userId=${userId}`);
  return response.data;
};

export const deleteBranch = async (branchId, userId) => {
  const response = await api.delete(`/branches/${branchId}`, { data: { userId } });
  return response.data;
};

export const updateBranch = async (branchId, updateData) => {
  const response = await api.patch(`/branches/${branchId}`, updateData);
  return response.data;
};

// Pull Requests API
export const getPullRequests = async (promptId, userId, status = 'all') => {
  const response = await api.get(`/pull-requests/prompt/${promptId}?status=${status}&userId=${userId}`);
  return response.data;
};

export const createPullRequest = async (prData) => {
  const response = await api.post('/pull-requests', prData);
  return response.data;
};

export const getPullRequest = async (prId, userId) => {
  const response = await api.get(`/pull-requests/${prId}?userId=${userId}`);
  return response.data;
};

export const addPRComment = async (prId, commentData) => {
  const response = await api.post(`/pull-requests/${prId}/comments`, commentData);
  return response.data;
};

export const submitPRReview = async (prId, reviewData) => {
  const response = await api.post(`/pull-requests/${prId}/reviews`, reviewData);
  return response.data;
};

export const mergePullRequest = async (prId, mergeData) => {
  const response = await api.post(`/pull-requests/${prId}/merge`, mergeData);
  return response.data;
};

export const closePullRequest = async (prId, userId) => {
  const response = await api.post(`/pull-requests/${prId}/close`, { userId });
  return response.data;
};

export const updatePullRequest = async (prId, updateData) => {
  const response = await api.patch(`/pull-requests/${prId}`, updateData);
  return response.data;
};

// Collaborators API
export const getCollaborators = async (promptId, userId) => {
  const response = await api.get(`/collaborators/prompt/${promptId}?userId=${userId}`);
  return response.data;
};

export const addCollaborator = async (collaboratorData) => {
  const response = await api.post('/collaborators/invite', collaboratorData);
  return response.data;
};

export const updateCollaborator = async (collaboratorId, updateData) => {
  const response = await api.patch(`/collaborators/${collaboratorId}`, updateData);
  return response.data;
};

export const removeCollaborator = async (collaboratorId, userId) => {
  const response = await api.delete(`/collaborators/${collaboratorId}`, { data: { userId } });
  return response.data;
};

export const getUserPermissions = async (userId, promptIds) => {
  const response = await api.get(`/collaborators/user/${userId}/permissions?promptIds=${promptIds.join(',')}`);
  return response.data;
};

export const searchUsers = async (query, promptId, userId, limit = 10) => {
  const response = await api.get(`/collaborators/search-users?query=${query}&promptId=${promptId}&userId=${userId}&limit=${limit}`);
  return response.data;
};

export const getCollaboratorActivity = async (promptId, userId, days = 30) => {
  const response = await api.get(`/collaborators/prompt/${promptId}/activity?userId=${userId}&days=${days}`);
  return response.data;
};

// Forks API
export const getForks = async (promptId) => {
  const response = await api.get(`/forks/prompt/${promptId}`);
  return response.data;
};

export const forkPrompt = async (forkData) => {
  const response = await api.post('/forks', forkData);
  return response.data;
};

export const getFork = async (forkId) => {
  const response = await api.get(`/forks/${forkId}`);
  return response.data;
};

export const getUserForks = async (userId) => {
  const response = await api.get(`/forks/user/${userId}`);
  return response.data;
};

export const getForkNetwork = async (promptId) => {
  const response = await api.get(`/forks/network/${promptId}`);
  return response.data;
};

export const deleteFork = async (forkId, userId) => {
  const response = await api.delete(`/forks/${forkId}`, { data: { userId } });
  return response.data;
};

export const checkForkAccess = async (promptId, userId) => {
  const response = await api.get(`/forks/check-access/${promptId}/${userId}`);
  return response.data;
};

// Auto-evaluation API functions (simplified)
export const getPromptLogEvaluationSummary = async (logId) => {
  const response = await api.get(`/auto-eval/summary/log/${logId}`);
  return response.data;
};

export const getLogsWithAutoEval = async (organizationId, filters = {}) => {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) queryParams.append(key, filters[key]);
  });
  
  const response = await api.get(`/auto-eval/logs/organization/${organizationId}?${queryParams}`);
  return response.data;
};

export const triggerLogEvaluation = async (logId) => {
  const response = await api.post(`/auto-eval/evaluate/log/${logId}`);
  return response.data;
};

// Prompt settings API functions
export const getPromptSettings = async (promptId) => {
  const response = await api.get(`/prompts/${promptId}/settings`);
  return response.data;
};

export const updatePromptSettings = async (promptId, settings) => {
  const response = await api.put(`/prompts/${promptId}/settings`, settings);
  return response.data;
};

export const deletePrompt = async (promptId, userId) => {
  const response = await api.delete(`/prompts/${promptId}?userId=${userId}`);
  return response.data;
};

export const getPromptLogs = async (promptId, filters = {}) => {
  const params = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
      params.append(key, filters[key]);
    }
  });
  const response = await api.get(`/prompts/${promptId}/logs?${params.toString()}`);
  return response.data;
};

export const getRecentActivity = async (organizationId, options = {}) => {
  const { limit = 20, days = 7 } = options;
  const response = await api.get(`/prompts/activity/${organizationId}?limit=${limit}&days=${days}`);
  return response.data;
};

// Project settings API functions
export const getProjectSettings = async (projectId) => {
  const response = await api.get(`/projects/${projectId}/settings`);
  return response.data;
};

export const updateProjectSettings = async (projectId, settings) => {
  const response = await api.put(`/projects/${projectId}/settings`, settings);
  return response.data;
};

// Search users for maintainers
export const searchUsersForMaintainers = async (query, organizationId) => {
  const response = await api.get(`/auth/search-users?query=${encodeURIComponent(query)}&organizationId=${organizationId}`);
  return response.data;
};

// ================================
// MVP Feature 8: Experiments API
// ================================

// Experiments Management
export const getExperiments = async (promptId, userId) => {
  const response = await api.get(`/experiments/prompt/${promptId}?userId=${userId}`);
  // Handle the standardized response format: { success: true, message: '...', data: [...] }
  return response.data?.data || [];
};

export const getUserExperiments = async (userId) => {
  const response = await api.get(`/experiments/user/${userId}`);
  // Handle the standardized response format: { success: true, message: '...', data: [...] }
  return response.data?.data || [];
};

export const getExperiment = async (experimentId, userId) => {
  const response = await api.get(`/experiments/${experimentId}?userId=${userId}`);
  return response.data.data || response.data;
};

export const createExperiment = async (experimentData) => {
  const response = await api.post('/experiments', experimentData);
  return response.data;
};

export const updateExperiment = async (experimentId, updateData) => {
  const response = await api.patch(`/experiments/${experimentId}`, updateData);
  return response.data;
};

export const deleteExperiment = async (experimentId, userId) => {
  const response = await api.delete(`/experiments/${experimentId}`, { data: { userId } });
  return response.data;
};

// Experiment Versions Management
export const addExperimentVersion = async (experimentId, versionData) => {
  const response = await api.post(`/experiments/${experimentId}/versions`, versionData);
  return response.data;
};

export const updateExperimentVersion = async (experimentId, versionId, updateData) => {
  const response = await api.patch(`/experiments/${experimentId}/versions/${versionId}`, updateData);
  return response.data;
};

export const removeExperimentVersion = async (experimentId, versionId, userId) => {
  const response = await api.delete(`/experiments/${experimentId}/versions/${versionId}`, { data: { userId } });
  return response.data;
};

// Experiment Analytics
export const getExperimentAnalytics = async (experimentId, userId, days = 30) => {
  const response = await api.get(`/experiments/${experimentId}/analytics?userId=${userId}&days=${days}`);
  return response.data;
};

// Experiment Routing (for SDK)
export const routeExperiment = async (promptId, stickyKey, stickyValue, apiKey) => {
  const response = await api.post('/experiments/route', {
    promptId,
    stickyKey,
    stickyValue,
    apiKey
  });
  return response.data;
};

// Dashboard Layout Management
export const saveDashboardLayout = async (userId, layout) => {
  const response = await api.post('/dashboard/layout', { userId, layout });
  return response.data;
};

export const getDashboardLayout = async (userId) => {
  const response = await api.get(`/dashboard/layout/${userId}`);
  return response.data;
};

// Notifications API
export const getNotificationCount = async (userId, type = null) => {
  const queryParams = type ? `?type=${type}` : '';
  const response = await api.get(`/notifications/user/${userId}/count${queryParams}`);
  return response.data;
};

export const getPendingInvites = async (userId) => {
  const response = await api.get(`/notifications/user/${userId}/invites?status=pending`);
  return response.data;
};

export const acceptInvite = async (inviteId, userId) => {
  const response = await api.post(`/notifications/invites/${inviteId}/accept`, { userId });
  return response.data;
};

export const rejectInvite = async (inviteId, userId, reason = '') => {
  const response = await api.post(`/notifications/invites/${inviteId}/reject`, { userId, reason });
  return response.data;
};

// Enhanced Experiment Analytics
export const getDetailedExperimentAnalytics = async (experimentId, userId, days = 30) => {
  const response = await api.get(`/experiments/${experimentId}/analytics/detailed?userId=${userId}&days=${days}`);
  return response.data.data || response.data;
};

export const getVersionAnalytics = async (experimentId, versionId, userId, days = 7) => {
  const response = await api.get(`/experiments/${experimentId}/versions/${versionId}/analytics?userId=${userId}&days=${days}`);
  return response.data.data || response.data;
};

// Flows API
export const getFlows = async (projectId) => {
  const response = await api.get(`/flows/project/${projectId}`);
  return response.data;
};

export const getFlow = async (flowId) => {
  const response = await api.get(`/flows/${flowId}`);
  return response.data;
};

export const createFlow = async (flowData) => {
  const response = await api.post('/flows', flowData);
  return response.data;
};

export const updateFlow = async (flowId, flowData) => {
  const response = await api.put(`/flows/${flowId}`, flowData);
  return response.data;
};

export const deleteFlow = async (flowId) => {
  const response = await api.delete(`/flows/${flowId}`);
  return response.data;
};

export const executeFlow = async (flowId, inputs = {}) => {
  const response = await api.post(`/flows/${flowId}/execute`, { inputs });
  return response.data;
};

export const getFlowExecution = async (flowId, executionId) => {
  const response = await api.get(`/flows/${flowId}/executions/${executionId}`);
  return response.data;
};

export const getFlowExecutions = async (flowId, limit = 20, offset = 0) => {
  const response = await api.get(`/flows/${flowId}/executions?limit=${limit}&offset=${offset}`);
  return response.data;
};

// Users API
export const getUserStats = async (userId) => {
  const response = await api.get(`/users/${userId}/stats`);
  return response.data;
};

// Recent Items API
export const getRecentProjects = async (userId, limit = 5) => {
  const response = await api.get(`/users/${userId}/recent/projects?limit=${limit}`);
  return response.data?.data || [];
};

export const getRecentPrompts = async (userId, limit = 5) => {
  const response = await api.get(`/users/${userId}/recent/prompts?limit=${limit}`);
  return response.data?.data || [];
};

export const getRecentExperiments = async (userId, limit = 5) => {
  const response = await api.get(`/users/${userId}/recent/experiments?limit=${limit}`); 
  return response.data?.data || [];
};

export const getFavoriteItems = async (userId) => {
  const response = await api.get(`/users/${userId}/favorites`);
  return response.data;
};

export const addToFavorites = async (userId, itemType, itemId) => {
  const response = await api.post(`/users/${userId}/favorites`, {
    itemType,
    itemId
  });
  return response.data;
};

export const removeFromFavorites = async (userId, itemType, itemId) => {
  const response = await api.delete(`/favorites/${userId}/${itemType}/${itemId}`);
  return response.data;
};

// Search API
export const searchAllItems = async (userId, query) => {
  const response = await api.get(`/search?userId=${userId}&query=${encodeURIComponent(query)}`);
  return response.data;
};

export default api; 